import { apiURL } from '../config/endpoints';

/**
 * Resolves a media URL returned by pic-service into a fetchable absolute URL.
 * This is a transport concern only — it says nothing about whether the result
 * is an image. To pick the URL for an `<img>`, use `mediaThumbUrl` below.
 *
 * Backend returns either:
 * - Relative path "/rails/active_storage/disk/..." (DiskStorage backend) — must
 *   route through the BFF proxy at /direct/picsvc.
 * - Absolute URL "http://localhost:3900/f2m3dev/..." (S3/Garage backend) —
 *   presigned URL pointing directly at the S3-compatible endpoint, used as-is.
 */
export function picUrl(url: string | null | undefined): string {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return apiURL + '/direct/picsvc' + url;
}

/** Minimal shape needed to pick a still image; satisfied by `Media` and `CartItemPic`. */
export interface ThumbnailSource {
  media_type?: string;
  url?: string | null;
  thumbnail_url?: string | null;
}

/**
 * Resolves the URL to use in an `<img src>` for a media item.
 *
 * `url` is the *display rendition*: a JPEG for a photo, but the **preview MP4
 * clip** for a video (pic-service attaches `preview`/`admin_preview` as video for
 * UserVideo — see Media::DerivativeGenerator). `thumbnail_url` is a still for
 * both kinds, so it is always the right choice for a thumbnail.
 *
 * When no thumbnail is attached yet (derivatives still processing) a photo
 * degrades to its display rendition, but a video degrades to no image at all —
 * putting the clip in an `<img>` is exactly the bug this helper exists to prevent
 * (GitLab #173).
 *
 * Use this for every `<img>`. For playback or a full-size zoom, reach for
 * `picUrl(media.url)` / `picUrl(media.original_url)` directly.
 */
export function mediaThumbUrl(media: ThumbnailSource | null | undefined): string {
  if (!media) return '';
  if (media.thumbnail_url) return picUrl(media.thumbnail_url);
  return media.media_type === 'video' ? '' : picUrl(media.url);
}
